import { useMemo } from 'react';
import { TeamInLeague, GameState } from '../domain/entities';
import { getPlayerValue, MAX_SQUAD_SIZE } from '../domain/constants';
import { calculateZoneStrengths } from '../services/MatchAnalysisService';

interface UseTeamMetricsProps {
    team: TeamInLeague;
    gameState: GameState;
    leagueName: string;
}

export const useTeamMetrics = ({ team, gameState, leagueName }: UseTeamMetricsProps) => {
    const metrics = useMemo(() => {
        const startingXI_Ids = Object.values(team.tactics.lineup).filter(id => id !== null) as number[];
        
        let zoneStrengths;
        if (startingXI_Ids.length < 11) {
            const bestAvailableIds = [...team.squad]
                .filter(p => p.injury?.type !== 'Severe') 
                .sort((a,b) => b.skill - a.skill)
                .slice(0, 11)
                .map(p => p.id);
            zoneStrengths = calculateZoneStrengths(team, bestAvailableIds);
        } else {
             zoneStrengths = calculateZoneStrengths(team, startingXI_Ids);
        }

        if (team.squad.length === 0) {
            return {
                ...zoneStrengths,
                attack: zoneStrengths.attack,
                mid: zoneStrengths.mid,
                def: zoneStrengths.def,
                avgAge: 0,
                totalValue: 0,
                weeklyWage: 0,
                injuredCount: 0,
            };
        }

        const avgAge = team.squad.reduce((sum, p) => sum + p.age, 0) / team.squad.length;
        const totalValue = team.squad.reduce((sum, p) => sum + getPlayerValue(p, leagueName, gameState.gameType, gameState.config), 0);
        const weeklyWage = team.squad.reduce((sum, p) => sum + p.wage, 0);
        const injuredCount = team.squad.filter(p => p.injury).length;

        return {
            attack: zoneStrengths.attack,
            mid: zoneStrengths.mid,
            def: zoneStrengths.def,
            avgAge,
            totalValue,
            weeklyWage,
            injuredCount,
        };
    }, [team, gameState, leagueName]);

    return metrics;
};
